import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ascch_token";

// Bearer-token auth: works across preview-URL changes without CORS credentials issues.
function applyToken(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    delete axios.defaults.headers.common["Authorization"];
    localStorage.removeItem(TOKEN_KEY);
  }
}

// Initialize from storage on module load
const _saved = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
if (_saved) axios.defaults.headers.common["Authorization"] = `Bearer ${_saved}`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=user
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (!tok) { setUser(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      applyToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.token) applyToken(data.token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatErr(e));
      return false;
    }
  };

  const register = async (name, email, password, termsAccepted = false) => {
    setError("");
    try {
      // Detect locale for welcome email (matches detectBrowserLocale logic in i18n.js)
      let locale = "de";
      try {
        const saved = localStorage.getItem("ch_lang");
        if (saved && ["de", "fr", "it", "en"].includes(saved)) locale = saved;
        else {
          const nav = (navigator.language || "de").slice(0, 2).toLowerCase();
          if (["de", "fr", "it", "en"].includes(nav)) locale = nav;
        }
        const m = window.location.pathname.match(/^\/(de|fr|it|en)(\/|$)/);
        if (m) locale = m[1];
      } catch { /* ignore */ }
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password, locale, terms_accepted: !!termsAccepted });
      if (data.token) applyToken(data.token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatErr(e));
      return false;
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    applyToken(null);
    setUser(false);
  };

  // Used after password-reset (or any out-of-band auth flow) to log the user in
  // with a fresh token + user payload returned by the backend.
  const applyAuthResponse = (data) => {
    if (!data) return;
    if (data.token) applyToken(data.token);
    setUser(data);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh: fetchMe, setError, applyAuthResponse }}>
      {children}
    </AuthContext.Provider>
  );
}

function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e.message || "Fehler";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return String(d);
}

export const useAuth = () => useContext(AuthContext);
export { API };
